/* eslint-disable react-hooks/exhaustive-deps */
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TApiLab, TLabFilter } from "../../../lib/types/labs";
import { useLabs } from "../../../services/laboratories";

import "leaflet/dist/leaflet.css";
import { Header } from "../../common/header";
import { useFileManagement } from "../../../services/file-management";
import { TGenericGUIDName, TGenericIdName, TProcessStates } from "../../../lib/types/general";
import { getRegionsWhereLabsAreHiring } from "../../../services/regions";
import { TRegion } from "../../../lib/types/regions";
import { DropdownMultiSelect, TOptionToShow } from "../../common/dropdown-multi-select";
import { TLabsFilterSettings } from "../../common/dental-lab-search-filter-panel";
import { getBusinessTypes, getIndustryRoles } from "../../../services/generalInfo";
import { Footer } from "../main-page/footer";
import { useAuthContext } from "../../../contexts/auth-context";
import { useRouteNavigation } from "../../../lib/common/routes";
import { ASSETS_ROUTES, DEVICES_SIZES_IN_PX, LAB_SEARCH_PAGINATION_SIZE } from "../../../lib/common/constants";
import { Pagination } from "../../common/pagination";
import { Spinner } from "../../common/spinner";
import { NotificationProvider } from "../../../contexts/notification-context";
import { evaluators, useWindowSizeMatch } from "../../../lib/window-size-matcher";
import { useMapLabLogic } from "../../common/map/map";
import { TEmployeeRole } from "../../../lib/types/employee_roles";
import { TBusinessTypes } from "../../../lib/types/business_types";

export const useHiringLabsLogic = () => {
    const {
        state,
        public: { getHiringLabs },
    } = useLabs();
    const [labs, setLabs] = useState<TApiLab[]>([]);
    const [industryRoles, setIndustryRoles] = useState<TGenericIdName[]>([]);
    const { isLoggedIn } = useAuthContext();

    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);

    const getLabs = async (filter: TLabFilter, offset: number) => {
        const data = await getHiringLabs(filter, offset);
        setLabs(data.data);
        setTotalCount(data.totalCount);
        setPageNumber(offset + 1);
        return data;
    };

    useEffect(() => {
        getIndustryRoles().then((industryRoles) => {
            setIndustryRoles(industryRoles);
        });
    }, []);

    return {
        workingState: state,
        labs,
        industryRoles,
        getLabs,
        showRegisteringInvitation: !isLoggedIn,
        totalCount,
        pageNumber,
    };
};

type LabInfoProps = {
    data: TApiLab;
    centerLabClickHandler: (lab: TApiLab) => void;
    industryRoles: TGenericIdName[];
    showRegisteringInvitation: boolean;
};
export const LabInfo = (props: LabInfoProps) => {
    const { data, centerLabClickHandler, industryRoles, showRegisteringInvitation } = props;
    const { userInfo } = useAuthContext();
    const { getImagePath } = useFileManagement();
    const { navigateRouteClickHandler } = useRouteNavigation();
    const notificationRef = useRef<any>();

    const idHashHue = (id: string) => {
        const l = id.length;
        let ret = 1;
        for (let i = 0; i < l; i++) {
            ret = ((id.charCodeAt(i) % 365) * ret) % 359;
        }

        return ret;
    };

    const imgPath = showRegisteringInvitation
        ? "/images/global/default_lab_logo3.jpeg"
        : data.images?.logo
          ? getImagePath(data.images.logo, "fixed", data.guid)
          : ASSETS_ROUTES.IMGS.labAvatarUrl;
    const imgLogoStyle: CSSProperties | undefined = showRegisteringInvitation
        ? {
              filter: `hue-rotate(${idHashHue(props.data.guid)}deg)`,
          }
        : undefined;
    // const industryRolesNames = data.other.positions
    //     .map((p) => industryRoles.find((v) => v.id === p)?.name)
    //     .join(", ");

    const clickHandler = (lab: TApiLab) => {
        if (notificationRef.current) {
            notificationRef.current.classList.add("card-notification-show");
        }
        centerLabClickHandler(lab);
    };
    const registrationMessage = "Please create an account to see dental employers details and connect with them.";

    return (
        <div className="col-12 p-0">
            <div
                data-testid="labInfoSmall"
                className={`scrollToLab-${data.id} text-decoration-none mb-2 w-100`}
                onClick={() => clickHandler(data)}
                role="button"
            >
                <div className="card card-maps w-100 rounded border">
                    <div className="row g-0">
                        <div className="col-3 text-center my-auto">
                            <img
                                className="rounded-3 maps-img m-2 m-md-3"
                                style={{
                                    ...imgLogoStyle,
                                }}
                                src={imgPath}
                                alt={data.name + " logo"}
                            />
                        </div>
                        <div className="col-9">
                            <div className="card-body">
                                <div className="text-decoration-none">
                                    <p
                                        className={"fw-bold m-0 employer-name mb-2 " + (showRegisteringInvitation ? "blurredText" : "")}
                                        title={showRegisteringInvitation ? registrationMessage : data.name}
                                    >
                                        {data.name}
                                        <small>{data.business_type_name}</small>
                                    </p>
                                    <p
                                        className="m-0 fw-semibold"
                                        style={{
                                            fontSize: "small",
                                        }}
                                    >
                                        Open positions:{" "}
                                    </p>
                                    <p
                                        className="m-0"
                                        style={{
                                            fontSize: "small",
                                        }}
                                    >
                                        {showRegisteringInvitation
                                            ? data.other.positions.length
                                            : data.other.positions.map((p) => industryRoles.find((v) => v.id === p.id)?.name).join(", ")}
                                    </p>
                                    {!showRegisteringInvitation && userInfo && userInfo.type === "tech" && (
                                        <div className="mt-1">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigateRouteClickHandler("dentalLabInfo", e, data.id.toString(), true);
                                                }}
                                                className="btn btn-info py-0 text-white"
                                                style={{
                                                    fontSize: "small",
                                                }}
                                            >
                                                Go to profile
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

type TSearchRowProps = {
    regions: TGenericGUIDName[];
    filterCallback: (filterSettings: TLabsFilterSettings) => void;
    initialRegionFromMainSection: string;
    positions: TEmployeeRole[];
    businessTypes: TBusinessTypes[];
};

type TQStringDecoded = {
    regions: {
        json: TOptionToShow[];
        value: string;
    };
    industryRoles: {
        json: TOptionToShow[];
        value: string;
    };
    businessTypes: {
        json: TOptionToShow[];
        value: string;
    };
};
const decodeQString = (): TQStringDecoded | undefined => {
    const qString = window.location.search;
    if (!qString) return undefined;

    const parameters = qString.split("?")[1].split("q=");
    if (parameters.length < 2) return undefined;
    const valueToDecode = JSON.parse(atob(decodeURIComponent(parameters[1])));
    return valueToDecode;
};

export const SearchRow = (props: TSearchRowProps) => {
    const { regions, filterCallback, initialRegionFromMainSection, positions, businessTypes } = props;
    const [regionsFilter, setRegionFilters] = useState<TOptionToShow[]>([]);
    const [industryRolesFilter, setIndustryRolesFilters] = useState<TOptionToShow[]>([]);
    const [businessTypesFilters, setBusinessTypesFilters] = useState<TOptionToShow[]>([]);
    const byDefaultFilterSettings: TQStringDecoded = {
        regions: {
            json: [],
            value: "",
        },
        industryRoles: {
            json: [],
            value: "",
        },
        businessTypes: {
            json: [],
            value: "",
        },
    };

    const invoqueFilterCallback = (qString: TQStringDecoded) => {
        filterCallback({
            names: [],
            regions: qString.regions?.json,
            industryRoles: qString.industryRoles?.json,
            businessTypes: qString.businessTypes?.json,
        });
    };

    const currRegionFilterCallback = (options: TOptionToShow[]) => {
        const qString: TQStringDecoded = decodeQString() || {
            ...byDefaultFilterSettings,
        };
        qString.regions = {
            json: options,
            value: "",
        };

        window.history.pushState({}, "", "?q=" + encodeURIComponent(btoa(JSON.stringify(qString))));
        invoqueFilterCallback(qString);
    };
    const currPositionsFilterCallback = (options: TOptionToShow[]) => {
        const qString: TQStringDecoded = decodeQString() || {
            ...byDefaultFilterSettings,
        };
        qString.industryRoles = {
            json: options,
            value: "",
        };

        window.history.pushState({}, "", "?q=" + encodeURIComponent(btoa(JSON.stringify(qString))));
        invoqueFilterCallback(qString);
    };
    const currBusinessTypesFilterCallback = (options: TOptionToShow[]) => {
        const qString: TQStringDecoded = decodeQString() || {
            ...byDefaultFilterSettings,
        };
        qString.businessTypes = {
            json: options,
            value: "",
        };

        window.history.pushState({}, "", "?q=" + encodeURIComponent(btoa(JSON.stringify(qString))));
        invoqueFilterCallback(qString);
    };

    const initialFilterSetup = (
        industryRolesData: TEmployeeRole[],
        regionsData: TGenericGUIDName[],
        businessTypes: TBusinessTypes[],
        filterValues?: TQStringDecoded,
    ) => {
        const regionsQS: TOptionToShow[] = filterValues?.regions?.json || [];
        const industryRolesQS: TOptionToShow[] = filterValues?.industryRoles?.json || [];
        const businessTypesQS: TOptionToShow[] = filterValues?.businessTypes?.json || [];

        const currRegionFilters = regionsData.map(
            (_data): TOptionToShow => ({
                data: {
                    id: _data.guid,
                    name: _data.name,
                },
                selected: regionsQS.findIndex((r) => r.data.id === _data.guid) > -1,
            }),
        );

        const currIndustryRolesFilters = industryRolesData.map(
            (data): TOptionToShow => ({
                data: {
                    id: data.id + "",
                    name: data.name,
                },
                selected: industryRolesQS.findIndex((i) => i.data.id === data.id + "") > -1,
            }),
        );

        const currBusinessTypes = businessTypes.map(
            (bt): TOptionToShow => ({
                data: {
                    id: bt.id + "",
                    name: bt.name,
                },
                selected: businessTypesQS.findIndex((i) => i.data.id === bt.id + "") > -1,
            }),
        );

        setRegionFilters(currRegionFilters);
        setIndustryRolesFilters(currIndustryRolesFilters);
        setBusinessTypesFilters(currBusinessTypes);

        const qStringData = filterValues || byDefaultFilterSettings;
        invoqueFilterCallback(qStringData);
    };

    const resetFiltersClickHandler = () => {
        window.history.pushState({}, "", window.location.pathname);
        initialFilterSetup(positions, regions, businessTypes, byDefaultFilterSettings);
    };

    useEffect(() => {
        const currFilters = decodeQString();
        let selectedRegions: TOptionToShow[] = [];
        let selectedBusinessTypes: TOptionToShow[] = [];
        if (currFilters?.regions && currFilters.regions.json.length > 0) {
            selectedRegions = currFilters.regions.json;
        } else {
            selectedRegions = [
                {
                    selected: true,
                    data: { id: initialRegionFromMainSection, name: "" },
                },
            ];
        }

        if (currFilters?.businessTypes && currFilters.businessTypes.json.length > 0) {
            selectedBusinessTypes = currFilters.businessTypes.json;
        }

        let selectedIndustryRoles: TOptionToShow[] = [];
        if (currFilters?.industryRoles && currFilters.industryRoles.json.length > 0) {
            selectedIndustryRoles = currFilters.industryRoles.json;
        }

        const filterSetup: TQStringDecoded = {
            industryRoles: {
                json: selectedIndustryRoles,
                value: "",
            },
            regions: {
                json: selectedRegions,
                value: "",
            },
            businessTypes: {
                json: selectedBusinessTypes,
                value: "",
            },
        };

        initialFilterSetup(positions, regions, businessTypes, filterSetup);
        window.history.pushState({}, "", "?q=" + encodeURIComponent(btoa(JSON.stringify(filterSetup))));
        invoqueFilterCallback(filterSetup);
    }, []);

    return (
        <>
            <div className="col-md-1 d-flex justify-content-end">
                <h6 className="d-none d-md-block py-2">
                    <i className="fa-solid fa-filter"></i> Filters:
                </h6>
            </div>
            <div className="col-md-3 col-lg-2 d-flex align-items-center justify-content-center">
                <div className="w-100">
                    <DropdownMultiSelect
                        title="Regions"
                        optionsToShow={regionsFilter}
                        setOptionsToShow={setRegionFilters}
                        applyFilterCallback={currRegionFilterCallback}
                        data-testid="region-filter"
                        btnColor="info"
                    />
                </div>
            </div>

            {industryRolesFilter.length > 0 && (
                <div className="col-md-3 col-lg-2 d-flex align-items-center justify-content-center">
                    {/* <i className="fa-solid fa-users-between-lines me-3 fa-xl"></i> */}
                    {/* <p className="d-none d-md-block mt-2 me-2">
                        Filter by open positions:
                    </p> */}
                    <div className="w-100">
                        <DropdownMultiSelect
                            btnColor="info"
                            title="Open Positions"
                            optionsToShow={industryRolesFilter}
                            setOptionsToShow={setIndustryRolesFilters}
                            applyFilterCallback={currPositionsFilterCallback}
                            data-testid="industry-filter"
                        />
                    </div>
                </div>
            )}
            {businessTypesFilters.length > 0 && (
                <div className="col-md-3 col-lg-2 align-item-center justify-content-center">
                    <div className="w-100">
                        <DropdownMultiSelect
                            btnColor="info"
                            title="Employer Type"
                            optionsToShow={businessTypesFilters}
                            setOptionsToShow={setBusinessTypesFilters}
                            applyFilterCallback={currBusinessTypesFilterCallback}
                            data-testid="business-filter"
                        />
                    </div>
                </div>
            )}
            <div className="col-md-2 col-lg-1 align-item-center justify-content-center">
                <button className="btn btn-outline-info border-2 fw-bold py-1 w-100" onClick={resetFiltersClickHandler} data-testid="reset-button">
                    Reset
                </button>
            </div>
        </>
    );
};

export const HiringLabs = () => {
    const theParams = useParams();
    const mapRef = useRef<any>();
    const mapCardsRef = useRef<HTMLDivElement>(null);
    const { workingState, labs, industryRoles, getLabs, showRegisteringInvitation, totalCount, pageNumber } = useHiringLabsLogic();

    const { centerLabOnMap, selectedLab, enableMap } = useMapLabLogic(mapRef);
    const [regions, setRegions] = useState<TRegion[]>([]);
    const [businessTypesList, setBusinessTypesList] = useState<TBusinessTypes[]>([]);
    const [roles, setRoles] = useState<TEmployeeRole[]>([]);
    const [workingStateHiringRegions, setWorkingStateHiringRegions] = useState<TProcessStates>("idle");
    const [filterSettings, setFilterSettings] = useState<TLabsFilterSettings>({
        names: [],
        regions: [],
        industryRoles: [],
        businessTypes: [],
    });
    const { navigateRouteClickHandler } = useRouteNavigation();

    const filterCallback = async (filterSettings: TLabsFilterSettings, offset: number) => {
        const dataLabs = await getLabs(
            {
                filterPositions: filterSettings.industryRoles.map((v) => Number(v.data.id)),
                filterRegions: filterSettings.regions.map((v) => v.data.id),
                businessTypesIds: filterSettings.businessTypes.map((v) => Number(v.data.id)),
            },
            offset,
        );
        enableMap(dataLabs.data);
        setFilterSettings(filterSettings);
    };
    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });
    useEffect(() => {
        setWorkingStateHiringRegions("working");

        Promise.all([getRegionsWhereLabsAreHiring(), getIndustryRoles(), getBusinessTypes({})]).then(([hiringRegions, positionsList, businessTypesList]) => {
            setRegions(hiringRegions.data);
            setRoles(positionsList);
            setBusinessTypesList(businessTypesList);
            setWorkingStateHiringRegions("done");
        });
    }, []);

    useEffect(() => {
        const filtersSection = mapCardsRef.current;
        if (!filtersSection) return;

        window.requestAnimationFrame(() => {
            filtersSection.scrollIntoView({
                block: "start",
                behavior: "smooth",
            });
        });
    }, []);

    return (
        <NotificationProvider>
            <Header title="" />
            <div className="container-fluid bg-dark pt-1 pb-0">
                <div className="container text-white text-center pb-2">
                    <h1 className="pb-2 pb-md-3 highlighted-title">Dental Employer Locator</h1>
                    <h6 className="pb-2 pb-md-4 mb-0">
                        Find the perfect dental employer near you or filter by open positions to discover your next opportunity!
                    </h6>
                </div>
            </div>
            {workingStateHiringRegions === "working" && (
                <div className="text-center mt-3">
                    <Spinner spinnerColor="text-info fa-xl" />
                </div>
            )}
            <div className="container-fluid">
                <div className="container px-2 px-md-3">
                    <div className="row g-0 g-md-2 justify-content-center py-2 py-md-3 mb-2" ref={mapCardsRef}>
                        {workingStateHiringRegions === "done" && (
                            <SearchRow
                                positions={roles}
                                initialRegionFromMainSection={theParams.regionId as string}
                                filterCallback={(filterSettings) => filterCallback(filterSettings, 0)}
                                businessTypes={businessTypesList}
                                regions={regions}
                            />
                        )}
                    </div>
                    <div className="row justify-content-center g-2 g-md-3">
                        {/* Map */}
                        <div
                            className="col-12 col-lg-5 rounded map-container mb-1 mb-md-4 px-0"
                            ref={mapRef}
                            style={isSmallDevice.fulfilled ? { height: "27vh", minHeight: "14rem" } : undefined}
                        ></div>
                        {/* Lab List / Subscribe */}
                        <div className="col-12 col-lg-5 lablist-map px-0 pb-3 pb-lg-0">
                            {showRegisteringInvitation && (
                                <div className="card p-3 map-overlay-card mb-2 mb-md-3 rounded border-2 border-primary text-center">
                                    <h2 className="fw-bold">Join now!</h2>
                                    <p className="m-0">Subscribe now to access the full list of dental employers and additional benefits!</p>
                                    <div className="row mt-4 ">
                                        <div className="col-12">
                                            <button
                                                className="btn btn-primary px-4 w-100 mb-2 text-white fw-bold"
                                                onClick={(e) => navigateRouteClickHandler("signupTech", e)}
                                            >
                                                SIGN UP AS DENTAL TALENT
                                            </button>
                                        </div>
                                        <div className="col-12">
                                            <button
                                                className="btn btn-primary px-4 w-100 text-white fw-bold"
                                                onClick={(e) => navigateRouteClickHandler("signupLab", e)}
                                            >
                                                SIGN UP AS DENTAL EMPLOYER
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {workingState === "working" && !showRegisteringInvitation && (
                                <div className="text-center">
                                    <Spinner spinnerColor="text-info fa-xl" />
                                </div>
                            )}
                            <div
                                className={`card map-overlay-card border-0 lablist-map__scroll ${
                                    showRegisteringInvitation ? " lablist-map__scroll__height_notLoggedIn" : " lablist-map__scroll__height_loggedIn"
                                }`}
                            >
                                {workingState !== "working" && (
                                    <div className="row px-0 px-lg-2 m-0">
                                        <>
                                            {!isSmallDevice.fulfilled && (
                                                <>
                                                    {
                                                        //myMap.map !== undefined &&
                                                        (workingState === "done" || workingState === "idle") &&
                                                            labs.map((lab, idx) => (
                                                                <LabInfo
                                                                    showRegisteringInvitation={showRegisteringInvitation}
                                                                    industryRoles={industryRoles}
                                                                    centerLabClickHandler={(lab: TApiLab) => {
                                                                        centerLabOnMap(lab);
                                                                    }}
                                                                    data={lab}
                                                                    key={`${lab.name}_${idx}_${lab}`}
                                                                />
                                                            ))
                                                    }
                                                    {totalCount > LAB_SEARCH_PAGINATION_SIZE && (
                                                        <div className="mt-3">
                                                            <Pagination
                                                                currentPage={pageNumber}
                                                                totalCount={totalCount}
                                                                itemsPerPage={LAB_SEARCH_PAGINATION_SIZE}
                                                                refObject={mapCardsRef}
                                                                onPageChange={(page: number) => {
                                                                    filterCallback(filterSettings, page);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            {isSmallDevice.fulfilled && (
                                                <>
                                                    {
                                                        //myMap.map !== undefined &&
                                                        (workingState === "done" || workingState === "idle") &&
                                                            (selectedLab ? (
                                                                <LabInfo
                                                                    showRegisteringInvitation={showRegisteringInvitation}
                                                                    industryRoles={industryRoles}
                                                                    centerLabClickHandler={(lab: TApiLab) => {
                                                                        centerLabOnMap(lab);
                                                                    }}
                                                                    data={selectedLab}
                                                                />
                                                            ) : (
                                                                ""
                                                            ))
                                                    }
                                                </>
                                            )}
                                        </>
                                    </div>
                                )}
                            </div>
                            {isSmallDevice.fulfilled && (
                                <div className="card map-pagination-card border-0 shadow-none bg-transparent mt-1">
                                    <p className="pt-2 pt-md-3 text-wrap fw-bold text-center mb-2">{labs.length} dental employers found within map area</p>
                                    <p className="px-2 px-md-3 text-center text-wrap mb-2">
                                        Looking for more dental employers? Use the filters to refine your search and click the pagination buttons below to
                                        navigate through additional listings!
                                    </p>
                                    <div className="mt-2 mt-md-3">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalCount={totalCount}
                                            itemsPerPage={LAB_SEARCH_PAGINATION_SIZE}
                                            refObject={mapCardsRef}
                                            onPageChange={(page: number) => {
                                                filterCallback(filterSettings, page);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </NotificationProvider>
    );
};
